import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/bi/AppShell";
import { Loading } from "@/components/bi/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Play, Save, Loader2, Wand2, CheckCircle2, AlertCircle, GripVertical, ChevronUp, ChevronDown, Undo2, Redo2, Sparkles, Database, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

const STEP_DEFS = {
  filter_rows: { label: "Filter rows", params: { column: "", op: "eq", value: "" } },
  calculated: { label: "Calculated column", params: { name: "", expr: "" } },
  rename: { label: "Rename column", params: { from: "", to: "" } },
  change_type: { label: "Change type", params: { column: "", to: "number" } },
  drop_columns: { label: "Remove columns", params: { columns: [] } },
  fillna: { label: "Fill missing values", params: { column: "", value: "" } },
  dedupe: { label: "Remove duplicates", params: { columns: [] } },
  sort: { label: "Sort rows", params: { column: "", direction: "asc" } },
  split_column: { label: "Split column", params: { column: "", delimiter: ",", into: [] } },
  replace_values: { label: "Replace values", params: { column: "", find: "", replace: "" } },
  case_transform: { label: "Trim / change case", params: { column: "", mode: "trim" } },
  group_aggregate: { label: "Group & aggregate", params: { group_by: [], aggs: [{ column: "", agg: "sum", alias: "" }] } },
  join: { label: "Join another dataset", params: { dataset_id: "", how: "left", keys: [{ left: "", right: "" }], suffix: "_joined" } },
  append: { label: "Append rows (union)", params: { dataset_id: "" } },
  pivot: { label: "Pivot (rows → columns)", params: { index: [], column: "", value: "", agg: "sum" } },
  unpivot: { label: "Unpivot (columns → rows)", params: { id_vars: [], value_vars: [], var_name: "attribute", value_name: "value" } },
};
const OPS = ["eq", "neq", "gt", "gte", "lt", "lte", "contains", "not_contains", "in", "is_null", "not_null"];
const AGGS = ["sum", "avg", "min", "max", "count", "count_distinct"];

export default function PrepStudio() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ds, setDs] = useState(null);
  const [steps, setSteps] = useState(null);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [cleanOpen, setCleanOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [matOpen, setMatOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [datasets, setDatasets] = useState([]);
  const dsName = ds?.name || "Dataset";

  useEffect(() => {
    api.get("/datasets").then(({ data }) => {
      setDatasets(data || []);
      setDs((data || []).find((d) => d.id === id) || null);
    }).catch(() => {});
    api.get(`/datasets/${id}/prep`).then(({ data }) => setSteps((data.steps || []).map((s) => ({ ...s, _k: crypto.randomUUID() })))).catch(() => setSteps([]));
  }, [id]);

  const runPreview = async (list) => {
    setRunning(true);
    try {
      const { data } = await api.post(`/datasets/${id}/prep/preview`, { steps: ((list ?? steps) || []).map(({ _k, ...s }) => s), limit: 100 });
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setRunning(false); }
  };
  const stepsLoaded = steps !== null;
  // run the first preview once steps have loaded
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (stepsLoaded) runPreview(steps); }, [stepsLoaded]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/datasets/${id}/prep`, { steps: (steps || []).map(({ _k, ...s }) => s) });
      toast.success("Prep steps applied — dataset schema updated");
      setDirty(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Some steps failed validation");
      if (d?.step_results) setPreview((p) => ({ ...(p || {}), step_results: d.step_results }));
    } finally { setSaving(false); }
  };

  const mutate = (next) => {
    setUndoStack((u) => [...u.slice(-49), steps]);
    setRedoStack([]);
    setSteps(next); setDirty(true);
  };
  const undo = () => {
    if (!undoStack.length) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack(undoStack.slice(0, -1));
    setRedoStack([...redoStack, steps]);
    setSteps(prev); setDirty(true);
  };
  const redo = () => {
    if (!redoStack.length) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack(redoStack.slice(0, -1));
    setUndoStack([...undoStack, steps]);
    setSteps(next); setDirty(true);
  };
  useEffect(() => {
    const h = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (e.key.toLowerCase() === "y" || (e.key.toLowerCase() === "z" && e.shiftKey)) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });
  const addStep = (type) => mutate([...(steps || []), { type, params: JSON.parse(JSON.stringify(STEP_DEFS[type].params)), enabled: true, _k: crypto.randomUUID() }]);
  const updateStep = (i, p) => mutate(steps.map((s, idx) => idx === i ? { ...s, ...p } : s));
  const updateParams = (i, patch) => updateStep(i, { params: { ...steps[i].params, ...patch } });
  const removeStep = (i) => mutate(steps.filter((_, idx) => idx !== i));
  const moveStep = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    mutate(next);
  };

  const columns = useMemo(() => preview?.columns || [], [preview]);
  const results = preview?.step_results || [];

  if (steps === null) return <AppShell><Loading /></AppShell>;

  return (
    <AppShell>
      <div className="px-6 py-5 max-w-[1500px] mx-auto" data-testid="prep-studio">
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <Button size="icon" variant="ghost" onClick={() => nav("/data")} data-testid="prep-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl text-[color:var(--ink)] flex items-center gap-2"><Wand2 className="w-5 h-5 text-[color:var(--brand-mint)]" />Data Prep — {dsName}</h1>
            <p className="text-xs text-[color:var(--ink-3)] mt-0.5">Applied steps run in order on every load (before calculated fields), for both snapshot and live datasets.</p>
          </div>
          <div className="flex items-center">
            <Button size="icon" variant="ghost" onClick={undo} disabled={!undoStack.length} data-testid="prep-undo-btn" title="Undo (Ctrl+Z)"><Undo2 className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" onClick={redo} disabled={!redoStack.length} data-testid="prep-redo-btn" title="Redo (Ctrl+Y)"><Redo2 className="w-4 h-4" /></Button>
          </div>
          <Button variant="outline" className="bg-[color:var(--surface)]" onClick={() => setCleanOpen(true)} data-testid="prep-autoclean-btn">
            <Sparkles className="w-4 h-4 mr-1 text-[color:var(--brand-mint)]" />Auto-clean
          </Button>
          <Button variant="outline" className="bg-[color:var(--surface)]" onClick={() => setAiOpen(true)} data-testid="prep-ai-btn">
            <Wand2 className="w-4 h-4 mr-1 text-[color:var(--brand-ocean)]" />AI steps
          </Button>
          <Button variant="outline" className="bg-[color:var(--surface)]" onClick={() => setMatOpen(true)} disabled={!steps.length} data-testid="prep-materialize-btn">
            <Database className="w-4 h-4 mr-1" />Save as dataset
          </Button>
          <Button variant="outline" className="bg-[color:var(--surface)]" onClick={() => runPreview()} disabled={running} data-testid="prep-preview-btn">
            {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}Preview
          </Button>
          <Button onClick={save} disabled={saving || !dirty} className="bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white" data-testid="prep-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save & apply
          </Button>
        </div>

        {ds?.source === "prep" && ds?.source_meta?.origin_dataset_id && (
          <div data-testid="prep-origin-banner" className="glass-card p-3 mb-4 space-y-2.5">
            <div className="flex items-center gap-3 text-sm flex-wrap">
              <Database className="w-4 h-4 text-[color:var(--brand-mint)] shrink-0" />
              <span className="text-[color:var(--ink-2)] flex-1 min-w-0">
                Prepared from <b>{ds.source_meta.origin_name || "source dataset"}</b>
                {ds.source_meta.materialized_at ? ` · last refreshed ${new Date(ds.source_meta.materialized_at).toLocaleString()}` : ""}
              </span>
              <Button size="sm" variant="outline" className="bg-[color:var(--surface)]" disabled={refreshing} data-testid="prep-refresh-materialized-btn"
                onClick={async () => {
                  setRefreshing(true);
                  try {
                    const { data } = await api.post(`/datasets/${id}/prep/rematerialize`);
                    toast.success(`Refreshed — ${Number(data.row_count).toLocaleString()} rows`);
                    const { data: list } = await api.get("/datasets");
                    setDs(list.find((d) => d.id === id) || ds);
                  } catch (e) {
                    const d = e?.response?.data?.detail;
                    toast.error(typeof d === "string" ? d : d?.message || "Refresh failed");
                  } finally { setRefreshing(false); }
                }}>
                {refreshing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Refresh from source
              </Button>
            </div>
            <PrepScheduleRow datasetId={id} schedule={ds.prep_refresh} onSaved={(sched) => setDs({ ...ds, prep_refresh: sched })} />
            <PrepJobsList datasetId={id} refreshKey={refreshing} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-5">
          {/* Applied steps */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold text-[color:var(--ink-2)]">Applied steps ({steps.length})</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="bg-[color:var(--surface)]" data-testid="prep-add-step-btn"><Plus className="w-4 h-4 mr-1" />Add step</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-h-80 overflow-auto">
                  {Object.entries(STEP_DEFS).map(([k, v]) => (
                    <DropdownMenuItem key={k} data-testid={`prep-step-${k}`} onClick={() => addStep(k)}>{v.label}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {steps.length === 0 && <div className="glass-card p-6 text-center text-sm text-[color:var(--ink-3)]">No steps yet — add filters, calculated columns, type changes and more.</div>}
            {steps.map((s, i) => {
              const res = results.find((r) => r.index === i);
              return (
                <div key={s._k || i} data-testid="prep-step-row" className={cn("glass-card p-3", s.enabled === false && "opacity-60")}>
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-3.5 h-3.5 text-[color:var(--ink-3)]" />
                    <span className="text-xs font-semibold text-[color:var(--ink)] flex-1">{i + 1}. {STEP_DEFS[s.type]?.label || s.type}</span>
                    {res && (res.ok
                      ? <Badge className="bg-[color:rgba(0,194,168,0.14)] text-[color:var(--success)] border-0 text-[10px]">{res.skipped ? "skipped" : `${res.rows} rows`}</Badge>
                      : <Badge data-testid="prep-step-error" className="bg-[color:rgba(232,93,106,0.14)] text-[color:var(--danger)] border-0 text-[10px]" title={res.error}><AlertCircle className="w-3 h-3 mr-0.5" />error</Badge>)}
                    <Switch checked={s.enabled !== false} onCheckedChange={(v) => updateStep(i, { enabled: v })} />
                    <button onClick={() => moveStep(i, -1)} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><ChevronUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => moveStep(i, 1)} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><ChevronDown className="w-3.5 h-3.5" /></button>
                    <button data-testid="prep-step-delete" onClick={() => removeStep(i)} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {res && !res.ok && <div className="text-[11px] text-[color:var(--danger)] mt-1 ml-6">{res.error}</div>}
                  <div className="mt-2 ml-6"><StepParams step={s} columns={columns} onChange={(p) => updateParams(i, p)} datasets={datasets.filter((d) => d.id !== id)} /></div>
                </div>
              );
            })}
          </div>

          {/* Preview */}
          <div className="glass-card p-4 min-h-[300px]">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs font-semibold text-[color:var(--ink-2)]">Preview</Label>
              {preview && <span className="text-[11px] text-[color:var(--ink-3)]">{preview.row_count?.toLocaleString()} rows · {columns.length} cols{preview.sampled ? " (sampled)" : ""}</span>}
            </div>
            {running && <div className="flex items-center gap-2 text-sm text-[color:var(--ink-3)] py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" />Applying steps…</div>}
            {!running && preview && (
              <div className="overflow-auto max-h-[62vh] rounded-lg border border-[color:var(--glass-border)]">
                <table className="w-full text-xs" data-testid="prep-preview-table">
                  <thead className="sticky top-0 bg-[color:var(--surface)] z-10">
                    <tr>{columns.map((c) => <th key={c} className="text-left px-3 pt-2 pb-1 font-semibold text-[color:var(--ink-2)] whitespace-nowrap">{c}</th>)}</tr>
                    {preview.profile && (
                      <tr data-testid="prep-profile-row">
                        {columns.map((c) => {
                          const pr = (preview.profile || []).find((p) => p.column === c);
                          if (!pr) return <th key={c} className="border-b border-[color:var(--glass-border)]" />;
                          return (
                            <th key={c} className="px-3 pb-2 text-left font-normal border-b border-[color:var(--glass-border)] align-top">
                              <div className="flex items-center gap-1">
                                <span className={cn("text-[9px] px-1 py-0.5 rounded font-medium",
                                  pr.kind === "number" ? "bg-[color:rgba(43,78,255,0.10)] text-[color:var(--brand-ocean)]"
                                    : pr.kind === "datetime" ? "bg-[color:rgba(0,194,168,0.12)] text-[color:var(--success)]"
                                      : "bg-[color:var(--surface-2)] text-[color:var(--ink-3)]")}>{pr.kind}</span>
                                <span className="text-[9px] text-[color:var(--ink-3)]">{pr.distinct} distinct</span>
                              </div>
                              <div className="flex items-center gap-1 mt-1" title={`${pr.nulls} nulls (${pr.null_pct}%)`}>
                                <div className="h-1 w-14 rounded-full bg-[color:var(--glass-border)] overflow-hidden">
                                  <div className="h-full bg-[color:var(--danger)]" style={{ width: `${Math.min(100, pr.null_pct)}%` }} />
                                </div>
                                <span className="text-[9px] text-[color:var(--ink-3)]">{pr.null_pct}% null</span>
                              </div>
                              {pr.kind === "number" && pr.min != null && (
                                <div className="text-[9px] text-[color:var(--ink-3)] mt-0.5 whitespace-nowrap">min {Number(pr.min).toLocaleString()} · max {Number(pr.max).toLocaleString()}</div>
                              )}
                            </th>
                          );
                        })}
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {(preview.rows || []).map((r, i) => (
                      <tr key={i} className="border-b border-[color:var(--glass-border)] last:border-0">
                        {columns.map((c) => <td key={c} className="px-3 py-1.5 text-[color:var(--ink-2)] whitespace-nowrap">{r[c] === null || r[c] === undefined ? <span className="text-[color:var(--ink-3)]">—</span> : String(r[c])}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!running && !preview && <div className="text-sm text-[color:var(--ink-3)] py-8 text-center">Click Preview to validate your steps against a data sample.</div>}
          </div>
        </div>

        <AutoCleanDialog open={cleanOpen} onOpenChange={setCleanOpen} datasetId={id} steps={steps}
          onAdd={(newSteps) => {
            const next = [...steps, ...newSteps.map((s) => ({ ...s, enabled: true, _k: crypto.randomUUID() }))];
            mutate(next);
            runPreview(next);
          }} />
        <AiPrepDialog open={aiOpen} onOpenChange={setAiOpen} datasetId={id} steps={steps}
          onAdd={(newSteps) => {
            const next = [...steps, ...newSteps.map((s) => ({ ...s, enabled: true, _k: crypto.randomUUID() }))];
            mutate(next);
            runPreview(next);
          }} />
        <MaterializeDialog open={matOpen} onOpenChange={setMatOpen} datasetId={id} steps={steps}
          defaultName={`${dsName} (prepared)`} onDone={() => nav("/data")} />
      </div>
    </AppShell>
  );
}

// One-click cleaning: backend analyzes the pipeline output and proposes safe steps.
function AutoCleanDialog({ open, onOpenChange, datasetId, steps, onAdd }) {
  const [loading, setLoading] = useState(false);
  const [sugg, setSugg] = useState(null);
  const [checked, setChecked] = useState({});
  useEffect(() => {
    if (!open) return;
    setLoading(true); setSugg(null);
    api.post(`/datasets/${datasetId}/prep/suggest`, { steps: (steps || []).map(({ _k, ...s }) => s) })
      .then(({ data }) => {
        setSugg(data.suggestions || []);
        setChecked(Object.fromEntries((data.suggestions || []).map((_, i) => [i, true])));
      })
      .catch((e) => {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Analysis failed");
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const selCount = Object.values(checked).filter(Boolean).length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="prep-autoclean-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="w-5 h-5 text-[color:var(--brand-mint)]" />Auto-clean suggestions</DialogTitle>
          <DialogDescription>Analyzed your data after the current steps — tick the fixes you want to add to the pipeline.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 flex items-center justify-center gap-2 text-sm text-[color:var(--ink-3)]"><Loader2 className="w-4 h-4 animate-spin" />Analyzing…</div>
        ) : sugg && !sugg.length ? (
          <div className="py-8 text-center text-sm text-[color:var(--ink-3)]"><CheckCircle2 className="w-6 h-6 mx-auto mb-1 text-[color:var(--success)]" />Your data already looks clean — nothing to suggest.</div>
        ) : (
          <div className="space-y-2 max-h-[46vh] overflow-auto">
            {(sugg || []).map((s, i) => (
              <label key={i} data-testid={`autoclean-suggestion-${i}`} className="flex items-start gap-2.5 rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2.5 cursor-pointer">
                <Checkbox data-testid={`autoclean-check-${i}`} checked={!!checked[i]} onCheckedChange={(v) => setChecked((c) => ({ ...c, [i]: !!v }))} className="mt-0.5" />
                <span className="min-w-0">
                  <span className="text-xs font-semibold text-[color:var(--ink)] block">{STEP_DEFS[s.step?.type]?.label || s.step?.type}{s.column ? ` — ${s.column}` : ""}</span>
                  <span className="text-[11px] text-[color:var(--ink-3)]">{s.reason}</span>
                </span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button data-testid="autoclean-apply-btn" size="sm" disabled={loading || !selCount}
            onClick={() => {
              const sel = (sugg || []).filter((_, i) => checked[i]).map((s) => ({ type: s.step.type, params: s.step.params }));
              onAdd(sel);
              toast.success(`${sel.length} cleaning step${sel.length > 1 ? "s" : ""} added — review and Save`);
              onOpenChange(false);
            }}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add {selCount} step{selCount === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Materialize the full pipeline output as a brand-new prepared dataset.
function MaterializeDialog({ open, onOpenChange, datasetId, steps, defaultName, onDone }) {
  const [name, setName] = useState(defaultName);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setName(defaultName); }, [open, defaultName]);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/datasets/${datasetId}/prep/materialize`, { name, steps: (steps || []).map(({ _k, ...s }) => s) });
      toast.success(`Dataset "${data.name}" created — ${Number(data.row_count).toLocaleString()} rows`);
      onOpenChange(false);
      onDone?.(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not create the dataset");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="prep-materialize-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Database className="w-5 h-5 text-[color:var(--brand-ocean)]" />Save as new dataset</DialogTitle>
          <DialogDescription>
            Runs the full pipeline on <b>all rows</b> and saves the result as a new snapshot dataset. It remembers its source, so you can refresh it any time.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs text-[color:var(--ink-3)]">New dataset name</Label>
          <Input data-testid="materialize-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-[color:var(--surface)]" />
        </div>
        <DialogFooter>
          <Button data-testid="materialize-run-btn" size="sm" onClick={run} disabled={busy || !name.trim()}>
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Create dataset
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Refresh schedule for a materialized dataset (runs in the background scheduler, UTC).
function PrepScheduleRow({ datasetId, schedule, onSaved }) {
  const s = schedule || {};
  const mode = s.enabled ? (s.mode || "daily") : "off";
  const save = async (patch) => {
    const next = { enabled: true, mode: s.mode || "daily", hour: s.hour ?? 6, weekday: s.weekday ?? 0, ...patch };
    if (patch.mode === "off") next.enabled = false;
    try {
      const { data } = await api.put(`/datasets/${datasetId}/prep/schedule`, {
        enabled: next.enabled, mode: next.enabled ? next.mode : (s.mode || "daily"),
        hour: Number(next.hour) || 0, weekday: Number(next.weekday) || 0,
      });
      onSaved(data);
      toast.success(next.enabled ? `Auto-refresh ${data.mode}${data.next_run_at ? ` — next run ${new Date(data.next_run_at).toLocaleString()}` : ""}` : "Auto-refresh turned off");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the schedule"); }
  };
  const small = "bg-[color:var(--surface)] h-7 text-xs w-auto";
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs text-[color:var(--ink-3)]" data-testid="prep-schedule-row">
      <span>Auto-refresh:</span>
      <Select value={mode} onValueChange={(v) => save(v === "off" ? { mode: "off" } : { mode: v, enabled: true })}>
        <SelectTrigger data-testid="prep-schedule-mode" className={small}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="off">Off</SelectItem>
          <SelectItem value="hourly">Hourly</SelectItem>
          <SelectItem value="daily">Daily</SelectItem>
          <SelectItem value="weekly">Weekly</SelectItem>
        </SelectContent>
      </Select>
      {s.enabled && (s.mode === "daily" || s.mode === "weekly") && (
        <Select value={String(s.hour ?? 6)} onValueChange={(v) => save({ hour: Number(v) })}>
          <SelectTrigger data-testid="prep-schedule-hour" className={small}><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-64">{Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00 UTC</SelectItem>)}</SelectContent>
        </Select>
      )}
      {s.enabled && s.mode === "weekly" && (
        <Select value={String(s.weekday ?? 0)} onValueChange={(v) => save({ weekday: Number(v) })}>
          <SelectTrigger data-testid="prep-schedule-weekday" className={small}><SelectValue /></SelectTrigger>
          <SelectContent>{["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {s.enabled && s.next_run_at && <span data-testid="prep-schedule-next">next run {new Date(s.next_run_at).toLocaleString()}</span>}
    </div>
  );
}

// Recent refresh jobs (manual + scheduled) for a materialized dataset.
function PrepJobsList({ datasetId, refreshKey }) {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get(`/datasets/${datasetId}/prep/jobs`).then(({ data }) => setJobs(data || [])).catch(() => {});
  }, [datasetId, refreshKey]);
  if (!jobs.length) return null;
  return (
    <div data-testid="prep-jobs-list">
      <button onClick={() => setOpen(!open)} className="text-[11px] text-[color:var(--brand-ocean)] hover:underline" data-testid="prep-jobs-toggle">
        {open ? "Hide" : "Show"} refresh history ({jobs.length})
      </button>
      {open && (
        <div className="mt-1.5 space-y-1">
          {jobs.map((j) => (
            <div key={j.id} className="flex items-center gap-2 text-[11px] text-[color:var(--ink-3)]">
              {j.status === "ok"
                ? <CheckCircle2 className="w-3 h-3 text-[color:var(--success)] shrink-0" />
                : j.status === "running"
                  ? <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                  : <AlertCircle className="w-3 h-3 text-[color:var(--danger)] shrink-0" />}
              <span>{new Date(j.started_at).toLocaleString()}</span>
              <Badge className="bg-[color:var(--surface-2)] text-[color:var(--ink-3)] border-0 text-[9px]">{j.trigger}</Badge>
              {j.status === "ok" ? <span>{Number(j.rows).toLocaleString()} rows</span> : j.error ? <span className="text-[color:var(--danger)] truncate max-w-[420px]" title={j.error}>{j.error}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ColSelect({ value, columns, onChange, placeholder = "column" }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="bg-[color:var(--surface)] h-8 text-xs"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-64">{columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function ColChips({ selected = [], columns, onChange }) {
  const toggle = (c) => onChange(selected.includes(c) ? selected.filter((x) => x !== c) : [...selected, c]);
  return (
    <div className="flex flex-wrap gap-1">
      {columns.map((c) => (
        <button key={c} type="button" onClick={() => toggle(c)}
          className={cn("text-[10px] px-2 py-0.5 rounded-full border transition-colors",
            selected.includes(c) ? "bg-[color:var(--brand-ocean)] text-white border-transparent" : "bg-[color:var(--surface-2)] text-[color:var(--ink-3)] border-[color:var(--glass-border)] hover:text-[color:var(--ink)]")}>{c}</button>
      ))}
    </div>
  );
}

function StepParams({ step, columns, onChange, datasets = [] }) {
  const p = step.params || {};
  const small = "bg-[color:var(--surface)] h-8 text-xs";
  switch (step.type) {
    case "filter_rows":
      return (
        <div className="grid grid-cols-3 gap-1.5">
          <ColSelect value={p.column} columns={columns} onChange={(v) => onChange({ column: v })} />
          <Select value={p.op || "eq"} onValueChange={(v) => onChange({ op: v })}>
            <SelectTrigger className={small}><SelectValue /></SelectTrigger>
            <SelectContent>{OPS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
          {!["is_null", "not_null"].includes(p.op) && <Input className={small} placeholder="value" value={p.value ?? ""} onChange={(e) => onChange({ value: e.target.value })} />}
        </div>
      );
    case "calculated":
      return (
        <div className="space-y-1.5">
          <Input className={small} placeholder="new column name" value={p.name || ""} onChange={(e) => onChange({ name: e.target.value })} data-testid="prep-calc-name" />
          <Textarea rows={2} className="bg-[color:var(--surface)] font-mono text-xs" placeholder="e.g. sales / units" value={p.expr || ""} onChange={(e) => onChange({ expr: e.target.value })} data-testid="prep-calc-expr" />
        </div>
      );
    case "rename":
      return (
        <div className="grid grid-cols-2 gap-1.5">
          <ColSelect value={p.from} columns={columns} onChange={(v) => onChange({ from: v })} />
          <Input className={small} placeholder="new name" value={p.to || ""} onChange={(e) => onChange({ to: e.target.value })} />
        </div>
      );
    case "change_type":
      return (
        <div className="grid grid-cols-2 gap-1.5">
          <ColSelect value={p.column} columns={columns} onChange={(v) => onChange({ column: v })} />
          <Select value={p.to || "number"} onValueChange={(v) => onChange({ to: v })}>
            <SelectTrigger className={small}><SelectValue /></SelectTrigger>
            <SelectContent>{["number", "string", "datetime", "boolean"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      );
    case "drop_columns":
      return <ColChips selected={p.columns || []} columns={columns} onChange={(v) => onChange({ columns: v })} />;
    case "fillna":
      return (
        <div className="grid grid-cols-2 gap-1.5">
          <ColSelect value={p.column} columns={columns} onChange={(v) => onChange({ column: v })} />
          <Input className={small} placeholder="fill value" value={p.value ?? ""} onChange={(e) => onChange({ value: e.target.value })} />
        </div>
      );
    case "dedupe":
      return (
        <div>
          <span className="text-[10px] text-[color:var(--ink-3)]">Match on (empty = all columns):</span>
          <ColChips selected={p.columns || []} columns={columns} onChange={(v) => onChange({ columns: v })} />
        </div>
      );
    case "sort":
      return (
        <div className="grid grid-cols-2 gap-1.5">
          <ColSelect value={p.column} columns={columns} onChange={(v) => onChange({ column: v })} />
          <Select value={p.direction || "asc"} onValueChange={(v) => onChange({ direction: v })}>
            <SelectTrigger className={small}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="asc">Ascending</SelectItem><SelectItem value="desc">Descending</SelectItem></SelectContent>
          </Select>
        </div>
      );
    case "split_column":
      return (
        <div className="grid grid-cols-3 gap-1.5">
          <ColSelect value={p.column} columns={columns} onChange={(v) => onChange({ column: v })} />
          <Input className={small} placeholder="delimiter" value={p.delimiter ?? ","} onChange={(e) => onChange({ delimiter: e.target.value })} />
          <Input className={small} placeholder="new names (a, b)" value={(p.into || []).join(", ")} onChange={(e) => onChange({ into: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
        </div>
      );
    case "replace_values":
      return (
        <div className="grid grid-cols-3 gap-1.5">
          <ColSelect value={p.column} columns={columns} onChange={(v) => onChange({ column: v })} />
          <Input className={small} placeholder="find" value={p.find ?? ""} onChange={(e) => onChange({ find: e.target.value })} />
          <Input className={small} placeholder="replace with" value={p.replace ?? ""} onChange={(e) => onChange({ replace: e.target.value })} />
        </div>
      );
    case "case_transform":
      return (
        <div className="grid grid-cols-2 gap-1.5">
          <ColSelect value={p.column} columns={columns} onChange={(v) => onChange({ column: v })} />
          <Select value={p.mode || "trim"} onValueChange={(v) => onChange({ mode: v })}>
            <SelectTrigger className={small}><SelectValue /></SelectTrigger>
            <SelectContent>{["trim", "upper", "lower", "title"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      );
    case "group_aggregate": {
      const aggs = p.aggs || [];
      const setAgg = (i, patch) => onChange({ aggs: aggs.map((a, idx) => idx === i ? { ...a, ...patch } : a) });
      return (
        <div className="space-y-1.5">
          <span className="text-[10px] text-[color:var(--ink-3)]">Group by:</span>
          <ColChips selected={p.group_by || []} columns={columns} onChange={(v) => onChange({ group_by: v })} />
          {aggs.map((a, i) => (
            <div key={i} className="grid grid-cols-[1fr_90px_1fr_auto] gap-1.5">
              <ColSelect value={a.column} columns={columns} onChange={(v) => setAgg(i, { column: v })} />
              <Select value={a.agg || "sum"} onValueChange={(v) => setAgg(i, { agg: v })}>
                <SelectTrigger className={small}><SelectValue /></SelectTrigger>
                <SelectContent>{AGGS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
              <Input className={small} placeholder="alias" value={a.alias || ""} onChange={(e) => setAgg(i, { alias: e.target.value })} />
              <button onClick={() => onChange({ aggs: aggs.filter((_, idx) => idx !== i) })} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button onClick={() => onChange({ aggs: [...aggs, { column: "", agg: "sum", alias: "" }] })} className="text-[11px] text-[color:var(--brand-ocean)] hover:underline flex items-center gap-1"><Plus className="w-3 h-3" />aggregation</button>
        </div>
      );
    }
    case "join":
      return <JoinParams p={p} columns={columns} onChange={onChange} datasets={datasets} small={small} />;
    case "append":
      return (
        <div>
          <span className="text-[10px] text-[color:var(--ink-3)]">Rows from this dataset are appended (columns matched by name):</span>
          <Select value={p.dataset_id || ""} onValueChange={(v) => onChange({ dataset_id: v })}>
            <SelectTrigger data-testid="prep-append-dataset" className={small + " mt-1"}><SelectValue placeholder="Dataset to append" /></SelectTrigger>
            <SelectContent className="max-h-64">{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      );
    case "pivot":
      return (
        <div className="space-y-1.5">
          <span className="text-[10px] text-[color:var(--ink-3)]">Keep as rows:</span>
          <ColChips selected={p.index || []} columns={columns} onChange={(v) => onChange({ index: v })} />
          <div className="grid grid-cols-3 gap-1.5">
            <div><span className="text-[10px] text-[color:var(--ink-3)]">Columns from</span><ColSelect value={p.column} columns={columns} onChange={(v) => onChange({ column: v })} /></div>
            <div><span className="text-[10px] text-[color:var(--ink-3)]">Values</span><ColSelect value={p.value} columns={columns} onChange={(v) => onChange({ value: v })} /></div>
            <div><span className="text-[10px] text-[color:var(--ink-3)]">Aggregate</span>
              <Select value={p.agg || "sum"} onValueChange={(v) => onChange({ agg: v })}>
                <SelectTrigger className={small}><SelectValue /></SelectTrigger>
                <SelectContent>{AGGS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
      );
    case "unpivot":
      return (
        <div className="space-y-1.5">
          <span className="text-[10px] text-[color:var(--ink-3)]">Keep as identifiers:</span>
          <ColChips selected={p.id_vars || []} columns={columns} onChange={(v) => onChange({ id_vars: v })} />
          <span className="text-[10px] text-[color:var(--ink-3)]">Unpivot these columns into rows:</span>
          <ColChips selected={p.value_vars || []} columns={columns} onChange={(v) => onChange({ value_vars: v })} />
          <div className="grid grid-cols-2 gap-1.5">
            <Input className={small} placeholder="attribute column name" value={p.var_name ?? "attribute"} onChange={(e) => onChange({ var_name: e.target.value })} />
            <Input className={small} placeholder="value column name" value={p.value_name ?? "value"} onChange={(e) => onChange({ value_name: e.target.value })} />
          </div>
        </div>
      );
    default:
      return null;
  }
}

// Join step editor: picks the other dataset, join type and key pairs (right columns
// come from the other dataset's saved schema).
function JoinParams({ p, columns, onChange, datasets, small }) {
  const other = datasets.find((d) => d.id === p.dataset_id);
  const rightCols = (other?.schema || []).map((c) => c.name);
  const keys = p.keys?.length ? p.keys : [{ left: "", right: "" }];
  const setKey = (i, patch) => onChange({ keys: keys.map((k, idx) => idx === i ? { ...k, ...patch } : k) });
  return (
    <div className="space-y-1.5">
      <div className="grid grid-cols-2 gap-1.5">
        <Select value={p.dataset_id || ""} onValueChange={(v) => onChange({ dataset_id: v, keys: [{ left: "", right: "" }] })}>
          <SelectTrigger data-testid="prep-join-dataset" className={small}><SelectValue placeholder="Dataset to join" /></SelectTrigger>
          <SelectContent className="max-h-64">{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={p.how || "left"} onValueChange={(v) => onChange({ how: v })}>
          <SelectTrigger data-testid="prep-join-how" className={small}><SelectValue /></SelectTrigger>
          <SelectContent>{["left", "inner", "right", "outer"].map((h) => <SelectItem key={h} value={h}>{h} join</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {keys.map((k, i) => (
        <div key={i} className="grid grid-cols-[1fr_auto_1fr_auto] gap-1.5 items-center">
          <ColSelect value={k.left} columns={columns} onChange={(v) => setKey(i, { left: v })} placeholder="this column" />
          <span className="text-[10px] text-[color:var(--ink-3)]">=</span>
          <ColSelect value={k.right} columns={rightCols} onChange={(v) => setKey(i, { right: v })} placeholder={other ? "their column" : "pick dataset first"} />
          <button onClick={() => onChange({ keys: keys.filter((_, idx) => idx !== i) })} disabled={keys.length === 1}
            className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] disabled:opacity-30"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button onClick={() => onChange({ keys: [...keys, { left: "", right: "" }] })} className="text-[11px] text-[color:var(--brand-ocean)] hover:underline flex items-center gap-1"><Plus className="w-3 h-3" />key pair</button>
    </div>
  );
}

// AI-assisted prep: natural-language instruction -> validated pipeline steps.
function AiPrepDialog({ open, onOpenChange, datasetId, steps, onAdd }) {
  const [instruction, setInstruction] = useState("");
  const [loading, setLoading] = useState(false);
  const [sugg, setSugg] = useState(null);
  const [checked, setChecked] = useState({});
  useEffect(() => { if (open) { setSugg(null); setInstruction(""); } }, [open]);
  const run = async () => {
    if (!instruction.trim()) return;
    setLoading(true); setSugg(null);
    try {
      const { data } = await api.post(`/datasets/${datasetId}/prep/ai-suggest`, {
        instruction: instruction.trim(), steps: (steps || []).map(({ _k, ...s }) => s),
      });
      setSugg(data.suggestions || []);
      setChecked(Object.fromEntries((data.suggestions || []).map((s, i) => [i, s.valid !== false])));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "AI suggestion failed");
    } finally { setLoading(false); }
  };
  const selCount = (sugg || []).filter((s, i) => checked[i] && s.valid !== false).length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="prep-ai-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Wand2 className="w-5 h-5 text-[color:var(--brand-ocean)]" />AI data prep</DialogTitle>
          <DialogDescription>Describe what you want in plain language — the AI proposes pipeline steps, validated against your columns before anything is added.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Input data-testid="prep-ai-instruction" value={instruction} onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !loading && run()}
            placeholder='e.g. "trim product names, drop rows with negative sales, add a profit margin column"'
            className="bg-[color:var(--surface)]" />
          <Button data-testid="prep-ai-run" size="sm" onClick={run} disabled={loading || !instruction.trim()} className="bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          </Button>
        </div>
        {sugg && (
          <div className="space-y-2 max-h-[40vh] overflow-auto">
            {sugg.map((s, i) => (
              <label key={i} data-testid={`prep-ai-suggestion-${i}`}
                className={cn("flex items-start gap-2.5 rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2.5",
                  s.valid === false ? "opacity-50" : "cursor-pointer")}>
                <Checkbox checked={!!checked[i] && s.valid !== false} disabled={s.valid === false}
                  onCheckedChange={(v) => setChecked((c) => ({ ...c, [i]: !!v }))} className="mt-0.5" />
                <span className="min-w-0">
                  <span className="text-xs font-semibold text-[color:var(--ink)] block">{STEP_DEFS[s.step?.type]?.label || s.step?.type}{s.column ? ` — ${s.column}` : ""}</span>
                  <span className="text-[11px] text-[color:var(--ink-3)]">{s.reason}</span>
                </span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button data-testid="prep-ai-apply" size="sm" disabled={!selCount}
            onClick={() => {
              const sel = (sugg || []).filter((s, i) => checked[i] && s.valid !== false).map((s) => ({ type: s.step.type, params: s.step.params }));
              onAdd(sel);
              toast.success(`${sel.length} AI step${sel.length > 1 ? "s" : ""} added — review and Save`);
              onOpenChange(false);
            }}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add {selCount} step{selCount === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
